import { useUpdateProject } from 'api/ProjectsAPI';
import { Project } from 'modules/customEvents/types';
import { ModalAction } from 'modules/dataDictionary/types';
import { createDefaultSettings, createSettingsPayload } from 'modules/dataDictionary/utils';
import { addNegativeToastWithErrorId, addPositiveToast } from 'modules/toastContainer';
import { FieldValues, useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useModalContext } from 'utils/contexts/modal';

export const SETTINGS_CONTENT_TEXT = {
  customEventFieldsSectionId: 'custom-event-fields-section',
  sectionTestId: 'settings-content',
  userFieldsSectionId: 'user-fields-section',
};

export const useSettingsContent = (project: Project, refetchProject: () => void) => {
  const dispatch = useDispatch();
  const { hideModal, showModal } = useModalContext();

  const {
    extendedProjectSettings: { purchaseHiddenFields = [], userHiddenFields = [] },
  } = project;

  const { formState, handleSubmit, reset } = useFormContext();

  /**
   * Update project
   */
  const { mutate: updateSettings } = useUpdateProject(project.id, {
    onError: (axiosError: any) => {
      const errorData = axiosError?.response?.data;
      const error = errorData?.error ? `- ${errorData.error}.` : '';
      const message = errorData?.message ? ` ${errorData.message}` : '';
      dispatch(
        addNegativeToastWithErrorId(
          `Error ${error}${message}`,
          'SchemaManagementSettingsContent',
          error,
        ),
      );
      refetchProject();
    },
    onSuccess: (_, values) => {
      const {
        allowNewCustomEvents,
        allowAutoUserFieldCreation,
        allowAutoCustomEventFieldCreation,
      } = values?.data?.dataSettings;
      dispatch(addPositiveToast('Done! Settings updated.'));
      refetchProject();
      const updatedProject = {
        ...project,
        extendedProjectSettings: { ...project.extendedProjectSettings, allowNewCustomEvents },
        allowAutoCustomEventFieldCreation,
        allowAutoUserFieldCreation,
      };
      const updatedDefaultValues = createDefaultSettings(updatedProject);
      reset(updatedDefaultValues);
      hideModal();
    },
  });

  /** Submit function when form fields are valid */
  const onValid = (data: FieldValues) => {
    // convert strings to booleans
    const basePayload = createSettingsPayload(data.userFieldValue, data.customEventValue);

    // add hidden fields to payload
    const dataSettings = {
      ...basePayload,
      purchaseHiddenFields,
      userHiddenFields,
    };

    updateSettings({ dataSettings });
  };

  const onSubmit = () =>
    showModal({
      action: ModalAction.SAVE_SETTINGS,
      payload: { onSave: handleSubmit(onValid) },
    });

  return { formState, onSubmit };
};
